import { useState } from 'react';
import { KeyRound, Loader2, CheckCircle, XCircle, Trash2, Edit2, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/auth.store';
import {
  useSsoConfig,
  useCreateSsoConfig,
  useUpdateSsoConfig,
  useDeleteSsoConfig,
  useTestDiscovery,
  type TestDiscoveryResult,
} from '@/api/hooks/useSso';
import { SsoConfigForm } from './SsoConfigForm';
import { useUserAdminActor } from '@/api/hooks/useSettings';

function TestResultBanner({ data }: { data: TestDiscoveryResult }) {
  if ('issuer' in data) {
    return (
      <div className="rounded-lg border border-status-success/20 bg-status-success/5 p-3">
        <p className="text-xs font-semibold text-status-success">OIDC discovery reachable</p>
        <p className="mt-1 font-mono text-xs text-text-secondary break-all">{data.issuer}</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-status-success/20 bg-status-success/5 p-3">
      <p className="text-xs font-semibold text-status-success">SAML metadata reachable</p>
      <p className="mt-1 text-xs text-text-secondary">
        Entity ID: <span className="font-mono break-all">{data.idp_entity_id}</span>
      </p>
      <p className="text-xs text-text-secondary">
        SSO URL: <span className="font-mono break-all">{data.idp_sso_url}</span>
      </p>
      {data.idp_signing_cert_thumbprint && (
        <p className="text-xs text-text-secondary">
          Cert SHA-256: <span className="font-mono break-all">{data.idp_signing_cert_thumbprint}</span>
        </p>
      )}
    </div>
  );
}

export function SsoConfigPage() {
  const { hasAnyRole } = useAuthStore();
  // Read: a super_admin or risk_manager. Write (configure / edit / test / delete): a super_admin only, as the server reads
  // it — the live role from GET /users/assignable-roles, not the token (USER_ADMIN cluster, the SSO door).
  const canManage = hasAnyRole(['super_admin', 'risk_manager']);
  const { data: actor } = useUserAdminActor();
  const canWrite = actor?.isSuperAdmin === true;

  const { data: config, isLoading, error } = useSsoConfig();
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const createMutation = useCreateSsoConfig();
  const updateMutation = useUpdateSsoConfig(config?.id ?? '');
  const deleteMutation = useDeleteSsoConfig(config?.id ?? '');
  const testMutation = useTestDiscovery(config?.id ?? '');

  if (!canManage) {
    return (
      <div className="flex flex-col gap-2 py-12 text-center">
        <KeyRound className="mx-auto h-10 w-10 text-text-muted" />
        <p className="text-sm text-text-secondary">Insufficient permissions to manage SSO configuration.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-text-secondary">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading SSO configuration…</span>
      </div>
    );
  }

  if (canWrite && (mode === 'create' || mode === 'edit')) {
    return (
      <div className="max-w-xl space-y-6">
        <div>
          <h2 className="text-base font-semibold text-text-primary">
            {mode === 'create' ? 'Configure Single Sign-On' : 'Edit SSO Configuration'}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Connect your identity provider to enable SSO for your team.
          </p>
        </div>

        <SsoConfigForm
          existing={mode === 'edit' ? config ?? undefined : undefined}
          onSubmit={async (data) => {
            if (mode === 'create') {
              await createMutation.mutateAsync(data as Parameters<typeof createMutation.mutateAsync>[0]);
            } else {
              await updateMutation.mutateAsync(data as Parameters<typeof updateMutation.mutateAsync>[0]);
            }
            setMode('view');
          }}
          onCancel={() => setMode('view')}
          isPending={createMutation.isPending || updateMutation.isPending}
        />
      </div>
    );
  }

  // ── View mode ───────────────────────────────────────────────────────────────

  if (!config) {
    return (
      <div className="max-w-xl">
        <div className="rounded-xl border border-dashed border-border-subtle bg-bg-surface p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-accent-cyan/10 mb-4">
            <KeyRound className="h-6 w-6 text-accent-cyan" />
          </div>
          <h2 className="text-base font-semibold text-text-primary">Single Sign-On not configured</h2>
          <p className="mt-2 text-sm text-text-secondary">
            Connect an OIDC or SAML 2.0 identity provider so your team can sign in via Microsoft Entra ID,
            Okta, Google Workspace, or any compatible IdP.
          </p>
          {error && (
            <p className="mt-2 text-xs text-status-error">
              {(error as any)?.response?.status === 404 ? null : 'Failed to load configuration'}
            </p>
          )}
          {canWrite ? (
            <Button className="mt-6" onClick={() => setMode('create')}>
              Configure SSO
            </Button>
          ) : (
            <p className="mt-6 text-xs text-text-muted">Only a super admin can configure Single Sign-On.</p>
          )}
        </div>
      </div>
    );
  }

  const meta = config.identity_provider_metadata;
  const protocolLabel = config.protocol === 'saml' ? 'SAML 2.0' : 'OpenID Connect (OIDC)';

  return (
    <div className="max-w-xl space-y-6">
      {/* Config card */}
      <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-status-success/10">
              <CheckCircle className="h-5 w-5 text-status-success" />
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary">{protocolLabel} SSO Active</p>
              <p className="text-xs text-text-secondary">
                Updated {new Date(config.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          {canWrite ? <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />}
              Test
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMode('edit')}>
              <Edit2 className="h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-status-error hover:text-status-error"
              onClick={() => setDeleteConfirm(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </div> : <p className="text-xs text-text-muted">Read-only — only a super admin can change Single Sign-On.</p>}
        </div>

        <dl className="space-y-3 text-sm">
          <div>
            <dt className="text-xs text-text-secondary">Protocol</dt>
            <dd className="mt-0.5 font-medium text-text-primary">{protocolLabel}</dd>
          </div>

          {config.protocol === 'oidc' && (
            <>
              <div>
                <dt className="text-xs text-text-secondary">Discovery URL</dt>
                <dd className="mt-0.5 break-all font-mono text-xs text-text-primary">
                  {String(meta.discovery_url ?? '')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-text-secondary">Client ID</dt>
                <dd className="mt-0.5 font-mono text-xs text-text-primary">
                  {String(meta.client_id ?? '')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-text-secondary">Client Secret</dt>
                <dd className="mt-0.5 flex items-center gap-1.5">
                  {config.has_secret
                    ? <><CheckCircle className="h-3.5 w-3.5 text-status-success" /><span className="text-xs text-text-secondary">Configured (write-only)</span></>
                    : <><XCircle className="h-3.5 w-3.5 text-status-error" /><span className="text-xs text-status-error">Not set</span></>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-text-secondary">Scopes</dt>
                <dd className="mt-0.5 font-mono text-xs text-text-primary">
                  {Array.isArray(meta.scopes) ? (meta.scopes as string[]).join(' ') : String(meta.scopes ?? '')}
                </dd>
              </div>
            </>
          )}

          {config.protocol === 'saml' && (
            <>
              <div>
                <dt className="text-xs text-text-secondary">IdP Entity ID</dt>
                <dd className="mt-0.5 break-all font-mono text-xs text-text-primary">
                  {String(meta.idp_entity_id ?? '')}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-text-secondary">IdP Metadata source</dt>
                <dd className="mt-0.5 text-xs text-text-primary">
                  {meta.idp_metadata_url
                    ? <span className="font-mono break-all">{String(meta.idp_metadata_url)}</span>
                    : <span className="text-text-secondary">XML (stored verbatim)</span>}
                </dd>
              </div>
            </>
          )}

          <div>
            <dt className="text-xs text-text-secondary">JIT Provisioning</dt>
            <dd className="mt-0.5 text-xs text-text-primary">
              {config.jit_provisioning_enabled
                ? 'Enabled — new users are created on first login'
                : 'Disabled — only pre-provisioned users can sign in'}
            </dd>
          </div>
          {config.role_claim_name && (
            <div>
              <dt className="text-xs text-text-secondary">Role Claim</dt>
              <dd className="mt-0.5 font-mono text-xs text-text-primary">{config.role_claim_name}</dd>
            </div>
          )}
        </dl>

        {testMutation.data && <TestResultBanner data={testMutation.data} />}
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="rounded-xl border border-status-error/30 bg-status-error/5 p-4 space-y-3">
          <p className="text-sm font-semibold text-status-error">Delete SSO configuration?</p>
          <p className="text-xs text-text-secondary">
            This disables SSO for your organisation. Existing SSO sessions remain active until they expire.
            Local password accounts continue to work.
          </p>
          <div className="flex gap-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={async () => { await deleteMutation.mutateAsync(); setDeleteConfirm(false); }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Yes, delete
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
